import React, { useCallback, useRef, useState } from 'react';
import { KatAlert, KatButton, KatModal } from '@amzn/katal-react';
import { useQueryClient } from 'react-query';
import { PendingThresholdChange } from 'src/features/types/r2fTypes';
import { FailedRequest } from 'src/features/types/gslaApi';
import { publishR2FThresholds } from 'src/utils/Queries';
import { useUser } from 'src/utils/UserContext';
import { R2FConfirmationModal } from './R2FConfirmationModal';
import { BulkR2FUploadRow } from '../../types/bulkR2fTypes';
import { parseR2FCSV, validateR2FData } from '../../utils/r2fCsvHelpers';
import { validateForDefaultPCSafety, logDefaultPCSafetyViolation, validatePSSDDefaultPCForBulkUpload } from '../../utils/defaultPCSafety';
import { getFCMetadata } from 'src/utils/Queries';
import { FailedRequestModal } from '../common/FailedRequestModal';

export const R2FUploadButton: React.FC = () => {
    const [isUploading, setIsUploading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<PendingThresholdChange[]>([]);
    const [uploadedData, setUploadedData] = useState<BulkR2FUploadRow[]>([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [failedRequests, setValidationErrors] = useState<FailedRequest[]>([]);
    const [showValidationErrorModal, setShowValidationErrorModal] = useState(false);

    const { user } = useUser();
    const queryClient = useQueryClient();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const text = await file.text();
            const parsedData = parseR2FCSV(text);
            validateR2FData(parsedData);

            // PSSD SAFETY CHECK: Reject DEFAULT_FC_PC rows for PSSD sites (run on raw CSV rows for correct row numbers)
            const pssdCheck = await validatePSSDDefaultPCForBulkUpload(parsedData, getFCMetadata);
            if (pssdCheck.shouldBlock) {
                throw new Error(pssdCheck.errorMessage);
            }

            setUploadedData(parsedData);

            const changes: PendingThresholdChange[] = parsedData
                .filter(row => {
                    const hasLowThreshold = row.newLowThreshold && !isNaN(Number(row.newLowThreshold)) && Number(row.newLowThreshold) >= 0;
                    const hasHighThreshold = row.newHighThreshold && !isNaN(Number(row.newHighThreshold)) && Number(row.newHighThreshold) >= 0;
                    return hasLowThreshold || hasHighThreshold;
                })
                .map(row => {
                    const hasNewLow = row.newLowThreshold && !isNaN(Number(row.newLowThreshold));
                    const hasNewHigh = row.newHighThreshold && !isNaN(Number(row.newHighThreshold));
                    const currentLow = row.currentLowThreshold && !isNaN(Number(row.currentLowThreshold)) ? Number(row.currentLowThreshold) : 0;
                    const currentHigh = row.currentHighThreshold && !isNaN(Number(row.currentHighThreshold)) ? Number(row.currentHighThreshold) : 0;
                    
                    return {
                        warehouseId: row.warehouseId,
                        productionCapability: row.productionCapability,
                        handlingTier: row.handlingTier,
                        lowThreshold: hasNewLow ? Number(row.newLowThreshold) : currentLow,
                        highThreshold: hasNewHigh ? Number(row.newHighThreshold) : currentHigh,
                        currentLowThreshold: currentLow,
                        currentHighThreshold: currentHigh,
                        isDefault: row.handlingTier.toLowerCase() === 'default',
                        userAlias: user?.login ?? 'unknown',
                        actionSource: 'BULK_UPLOAD',
                        reason: 'Bulk CSV Upload',
                        action: 'Modify' as const
                    };
                });

            // CRITICAL SAFETY CHECK: Validate for Default_PC entries
            const safetyCheck = validateForDefaultPCSafety(changes, 'R2F', 'CSV Upload');
            
            if (safetyCheck.shouldBlock) {
                // Log the safety violation for audit purposes
                logDefaultPCSafetyViolation(
                    'R2F',
                    user?.login || 'unknown',
                    safetyCheck.defaultPCCount,
                    'CSV Upload'
                );
                
                throw new Error(`CRITICAL SAFETY BLOCK: Your CSV contains ${safetyCheck.defaultPCCount} "Default_PC" entries. "Default_PC" cannot be published as it would cause serious system issues. Please remove all "Default_PC" entries from your CSV file and try again.`);
            }

            if (changes.length > 0) {
                setPendingChanges(changes);
                setIsModalOpen(true);
            } else {
                throw new Error('No valid threshold values found in CSV');
            }

        } catch (error) {
            console.error('Error processing CSV file:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to process CSV file');
            setShowErrorModal(true);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [user?.login]);

    const handleConfirmChanges = async () => {
        try {
            // ADDITIONAL SAFETY CHECK: Double-validate before final publish
            const finalSafetyCheck = validateForDefaultPCSafety(pendingChanges, 'R2F', 'Final Validation');
            
            if (finalSafetyCheck.shouldBlock) {
                // This should not happen if the earlier validation worked, but it's a safety net
                throw new Error(`CRITICAL SAFETY ERROR: Default_PC detected in final publish validation. Operation aborted.`);
            }
            
            console.log('Publishing R2F values with pendingChanges:', pendingChanges);
            
            // Group changes by warehouse for efficient API calls
            const changesByWarehouse = pendingChanges.reduce((acc, change) => {
                const warehouseId = change.warehouseId || 'DEFAULT';
                if (!acc[warehouseId]) {
                    acc[warehouseId] = [];
                }
                acc[warehouseId].push(change);
                return acc;
            }, {} as Record<string, PendingThresholdChange[]>);

            // Process each warehouse's changes and collect validation errors
            const allFailedRequests: FailedRequest[] = [];
            for (const [warehouseId, warehouseChanges] of Object.entries(changesByWarehouse)) {
                const r2fToPublishList = prepareThresholdPublishRequest(warehouseChanges, warehouseId, user?.login || 'unknown');
                const response = await publishR2FThresholds({ r2fToPublishList });
                if (response.errors && response.errors.length > 0) {
                    allFailedRequests.push(...response.errors);
                }
            }
            
            // Invalidate the relevant queries to refresh the main table
            await queryClient.invalidateQueries(['entire-network-r2f-thresholds']);
            await queryClient.invalidateQueries(['bulk-r2f-thresholds']);
            
            // Also invalidate specific warehouse queries
            Object.keys(changesByWarehouse).forEach(warehouseId => {
                queryClient.invalidateQueries(['r2f-thresholds', warehouseId]);
            });
            
            setIsModalOpen(false);
            setPendingChanges([]);
            setUploadedData([]);

            // Check for validation errors across all publish responses
            if (allFailedRequests.length > 0) {
                setValidationErrors(allFailedRequests);
                setShowValidationErrorModal(true);
            } else {
                setShowSuccessModal(true);
            }
        } catch (error) {
            console.error('Error publishing values:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save changes. Please try again.');
            setIsModalOpen(false);
            setShowErrorModal(true);
        }
    };

    const prepareThresholdPublishRequest = (
        changes: PendingThresholdChange[],
        warehouseId: string,
        userAlias: string
    ) => {
        // Group changes by production capability
        const changesByPC: Record<string, PendingThresholdChange[]> = {};
        
        changes.forEach(change => {
            const pc = change.productionCapability;
            if (!changesByPC[pc]) {
                changesByPC[pc] = [];
            }
            changesByPC[pc].push(change);
        });
        
        // Create the publish request
        const thresholdsToPublish: any = {};
        
        // Process each production capability
        Object.entries(changesByPC).forEach(([pc, pcChanges]) => {
            if (!thresholdsToPublish[pc]) {
                thresholdsToPublish[pc] = {};
            }
            
            // Process each change for this production capability
            pcChanges.forEach(change => {
                const threshold: any = {
                    userAlias: change.userAlias || userAlias,
                    reason: change.reason || 'Bulk R2F Update',
                    publishDateEpochMillis: Date.now(),
                    actionSource: change.actionSource || 'Newton Dashboard Bulk R2F'
                };

                // Only include thresholds that have values
                if (change.lowThreshold !== undefined) {
                    threshold.lowThreshold = change.lowThreshold;
                }
                if (change.highThreshold !== undefined) {
                    threshold.highThreshold = change.highThreshold;
                }
                
                thresholdsToPublish[pc][change.handlingTier] = threshold;
            });
        });
        
        return [{
            warehouseId,
            r2fToPublish: thresholdsToPublish
        }];
    };

    const handleButtonClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleModalClose = useCallback(() => {
        setIsModalOpen(false);
        setPendingChanges([]);
        setUploadedData([]);
    }, []);

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv"
                style={{ display: 'none' }}
            />
            <KatButton
                variant="primary"
                onClick={handleButtonClick}
                disabled={isUploading}
            >
                {isUploading ? 'Uploading...' : 'Upload CSV'}
            </KatButton>

            <R2FConfirmationModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                pendingChanges={pendingChanges}
                uploadedData={uploadedData}
                onConfirm={handleConfirmChanges}
                isUploading={isUploading}
            />

            {/* Success Modal */}
            <KatModal
                visible={showSuccessModal}
                title="Success"
            >
                <div>
                    <p>R2F threshold changes have been saved successfully</p>
                    <div>
                        <KatButton
                            onClick={() => setShowSuccessModal(false)}
                            variant="primary"
                        >
                            OK
                        </KatButton>
                    </div>
                </div>
            </KatModal>

            {/* Error Modal */}
            <KatModal
                visible={showErrorModal}
                title="Error"
            >
                <div>
                    <p>An error occurred while saving R2F threshold changes. Please try again.</p>
                    <p style={{ color: '#D13212' }}>{errorMessage}</p>
                    <div>
                        <KatButton
                            onClick={() => setShowErrorModal(false)}
                            variant="primary"
                        >
                            OK
                        </KatButton>
                    </div>
                </div>
            </KatModal>
            {/* Validation Error Modal - shown on partial success */}
            <FailedRequestModal
                visible={showValidationErrorModal}
                onClose={() => setShowValidationErrorModal(false)}
                errors={failedRequests}
            />
        </>
    );
};
